import type { z } from "zod";
import { API_BASE_PATH, ASSISTANT_BLOCKS_HEADER } from "@teamna/shared";
import { apiOrigin } from "./base-url.ts";

/**
 * The Teamna API client for the web app.
 *
 * Mirrors `apps/mobile/src/api/client.ts` so the two surfaces cannot drift: the same bearer
 * token, the same problem+json parsing into a stable `code`, the same `{ items: [] }` unwrapping,
 * the same Idempotency-Key on anything that spends money or a credit.
 *
 * One thing is stricter here. Every response is parsed with the zod schema from `@teamna/shared`
 * that the API validates its own output against, so a screen receives a typed value rather than a
 * cast, and a contract change shows up as one clear error instead of `undefined` three components
 * deep.
 */

const DEFAULT_TIMEOUT_MS = 20_000;

/** A parsed RFC 7807 problem+json response, or a client-side failure wearing the same shape. */
export class ApiError extends Error {
  readonly code: string;
  readonly title: string;
  readonly detail: string;
  readonly status: number;
  /** The problem's `meta`, when it has one: for example the refused lines of a batch booking. */
  readonly meta: Record<string, unknown> | undefined;

  constructor(params: { code: string; title: string; detail: string; status: number; meta?: Record<string, unknown> }) {
    super(params.detail || params.title || params.code);
    this.name = "ApiError";
    this.code = params.code;
    this.title = params.title;
    this.detail = params.detail;
    this.status = params.status;
    this.meta = params.meta;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

type TokenGetter = () => string | null;

let tokenGetter: TokenGetter = () => null;
let onUnauthorized: (() => void) | null = null;

/**
 * Registers where the bearer token comes from. Called once from `main.tsx`. The client is a plain
 * module and cannot read a React store directly, and the indirection is also what lets a test hand
 * it a fixed token.
 */
export function setTokenGetter(getter: TokenGetter): void {
  tokenGetter = getter;
}

/**
 * Registers what happens on a 401. The app clears the stored token and lets the router send the
 * user back to sign-in; the client itself does not know about routing.
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

/**
 * Where requests go and what they carry, beyond the bearer token.
 *
 * Desk uses the defaults: `/v1` on the page's own origin, no extra headers, the browser's default
 * credentials. Control Center renders the same owner screens with the same hooks while acting as a
 * business, so it points the client at `/v1/desk`, adds `X-Act-As-Business`, and sends its session
 * cookie with `credentials: "include"`. Configuring the one client keeps every hook shared rather
 * than forked.
 */
export type ApiClientConfig = {
  /** The path every endpoint path is relative to. `/v1` unless set. */
  basePath: string;
  /** Extra headers for every request, read at request time. */
  headers?: () => Record<string, string>;
  /** `fetch` credentials mode. Unset leaves the browser default (`same-origin`). */
  credentials?: RequestCredentials;
};

const DEFAULT_CONFIG: ApiClientConfig = { basePath: API_BASE_PATH };
let sharedConfig: ApiClientConfig = DEFAULT_CONFIG;

/** Changes where the shared `api` client (and every hook built on it) sends requests. */
export function configureApiClient(next: Partial<ApiClientConfig>): void {
  sharedConfig = { ...sharedConfig, ...next };
}

/** Back to the Desk defaults. Tests call this between cases. */
export function resetApiClientConfig(): void {
  sharedConfig = DEFAULT_CONFIG;
}

export function apiClientConfig(): ApiClientConfig {
  return sharedConfig;
}

/** RFC 4122-ish v4 id, good enough for an Idempotency-Key header. */
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  const hex = "0123456789abcdef";
  let out = "";
  for (let index = 0; index < 32; index += 1) out += hex[Math.floor(Math.random() * 16)];
  return [
    out.slice(0, 8),
    out.slice(8, 12),
    `4${out.slice(13, 16)}`,
    `a${out.slice(17, 20)}`,
    out.slice(20, 32),
  ].join("-");
}

/** 401s that are about a one-time code, not about the bearer token. */
const SIGN_IN_CODE_ERRORS = new Set(["OTP_INVALID", "INVALID_CREDENTIALS"]);

export type QueryValue = string | number | boolean | null | undefined;

export type RequestOptions = {
  query?: Record<string, QueryValue>;
  /** Send an Idempotency-Key. Set on every booking, cancel, reschedule and checkout. */
  idempotencyKey?: string;
  timeoutMs?: number;
  signal?: AbortSignal;
};

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

function buildPath(path: string, query: RequestOptions["query"]): string {
  if (!query) return path;
  const parts: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  if (parts.length === 0) return path;
  return `${path}${path.includes("?") ? "&" : "?"}${parts.join("&")}`;
}

/** Turns any non-2xx body into an ApiError, tolerating bodies that are not problem+json. */
async function toApiError(response: Response): Promise<ApiError> {
  let code = `HTTP_${response.status}`;
  let title = "Request failed";
  let detail = "";
  let meta: Record<string, unknown> | undefined;

  try {
    const text = await response.text();
    if (text) {
      const parsed: unknown = JSON.parse(text);
      if (parsed && typeof parsed === "object") {
        const problem = parsed as Record<string, unknown>;
        if (typeof problem.code === "string") code = problem.code;
        if (typeof problem.title === "string") title = problem.title;
        if (typeof problem.detail === "string") detail = problem.detail;
        if (!detail && typeof problem.message === "string") detail = problem.message;
        if (problem.meta && typeof problem.meta === "object") meta = problem.meta as Record<string, unknown>;
      }
    }
  } catch {
    // Body was not JSON. The status-derived code is still useful.
  }

  if (response.status === 401) {
    // The session is the problem, not the request. `code` stays whatever the server said when it
    // said something specific, but the app needs one reliable signal to sign the user out on.
    if (code === "HTTP_401") code = "SESSION_EXPIRED";
  } else if (response.status === 403 && code === "HTTP_403") {
    code = "FORBIDDEN";
  }

  return new ApiError({ code, title, detail, status: response.status, ...(meta ? { meta } : {}) });
}

/** Unwraps the API's list envelope. Collection endpoints answer `{ items: [...] }`, a few bare. */
export function toArray(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    const items = (payload as { items?: unknown }).items;
    if (Array.isArray(items)) return items;
  }
  return [];
}

function parseWith<S extends z.ZodTypeAny>(schema: S, payload: unknown, path: string): z.infer<S> {
  const result = schema.safeParse(payload);
  if (result.success) return result.data;
  const first = result.error.issues[0];
  const where = first ? `${first.path.join(".") || "(root)"}: ${first.message}` : "unknown field";
  throw new ApiError({
    code: "RESPONSE_SHAPE",
    title: "Unexpected response",
    detail: `${path} did not match the contract (${where})`,
    status: 0,
  });
}

async function request(
  config: ApiClientConfig,
  method: Method,
  path: string,
  body: unknown,
  options: RequestOptions = {},
  answer: "json" | "text" = "json",
): Promise<unknown> {
  const { query, idempotencyKey, timeoutMs = DEFAULT_TIMEOUT_MS } = options;
  const fullPath = buildPath(path, query);

  const headers: Record<string, string> = {
    Accept: answer === "json" ? "application/json" : "*/*",
    // Desk ships with the API, so it draws every block type (AI Assistant charts).
    [ASSISTANT_BLOCKS_HEADER]: "chart",
    ...(config.headers?.() ?? {}),
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;

  const token = tokenGetter();
  if (token) headers.Authorization = `Bearer ${token}`;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  if (options.signal) {
    options.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }

  let response: Response;
  try {
    response = await fetch(`${apiOrigin()}${config.basePath}${fullPath}`, {
      method,
      headers,
      ...(config.credentials ? { credentials: config.credentials } : {}),
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
  } catch (error) {
    const aborted = error instanceof Error && error.name === "AbortError";
    throw new ApiError({
      code: aborted ? "TIMEOUT" : "NETWORK_ERROR",
      title: aborted ? "Request timed out" : "Network error",
      detail: aborted ? "The server took too long to answer." : "We could not reach the server.",
      status: 0,
    });
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    const error = await toApiError(response);
    // A wrong sign-in code is a 401 too, but it says nothing about the session: a signed-in user
    // adding an email must not be signed out for a typo.
    if (error.status === 401 && !SIGN_IN_CODE_ERRORS.has(error.code)) onUnauthorized?.();
    throw error;
  }

  if (response.status === 204) return undefined;
  const text = await response.text();
  if (answer === "text") return text;
  if (!text) return undefined;
  return JSON.parse(text) as unknown;
}

/**
 * Every call names the shared schema its answer has to satisfy.
 *
 *   const me = await api.get("/me", meResponseSchema);
 *   const groups = await api.list("/me/schedule", scheduleGroupSchema, { query: { from, to } });
 */
export function createApiClient(config: () => ApiClientConfig) {
  return {
    get: async <S extends z.ZodTypeAny>(
      path: string,
      schema: S,
      options?: RequestOptions,
    ): Promise<z.infer<S>> => parseWith(schema, await request(config(), "GET", path, undefined, options), path),

    /** A file the API makes, as text: a report export (CSV). Errors are problem+json, as everywhere. */
    getText: async (path: string, options?: RequestOptions): Promise<string> =>
      String((await request(config(), "GET", path, undefined, options, "text")) ?? ""),

    /** A collection, unwrapped from `{ items }` and parsed item by item. */
    list: async <S extends z.ZodTypeAny>(
      path: string,
      itemSchema: S,
      options?: RequestOptions,
    ): Promise<z.infer<S>[]> => {
      const payload = await request(config(), "GET", path, undefined, options);
      const rows = toArray(payload);
      return rows.map((row, index) => parseWith(itemSchema, row, `${path}[${index}]`));
    },

    post: async <S extends z.ZodTypeAny>(
      path: string,
      body: unknown,
      schema: S,
      options?: RequestOptions,
    ): Promise<z.infer<S>> => parseWith(schema, await request(config(), "POST", path, body, options), path),

    /** A whole-resource save: the timetable and the packages page send the full list each time. */
    put: async <S extends z.ZodTypeAny>(
      path: string,
      body: unknown,
      schema: S,
      options?: RequestOptions,
    ): Promise<z.infer<S>> => parseWith(schema, await request(config(), "PUT", path, body, options), path),

    patch: async <S extends z.ZodTypeAny>(
      path: string,
      body: unknown,
      schema: S,
      options?: RequestOptions,
    ): Promise<z.infer<S>> => parseWith(schema, await request(config(), "PATCH", path, body, options), path),

    delete: async (path: string, options?: RequestOptions): Promise<void> => {
      await request(config(), "DELETE", path, undefined, options);
    },

    /** A DELETE that answers with a body: the family after a change, or what to undo a removal with. */
    remove: async <S extends z.ZodTypeAny>(
      path: string,
      schema: S,
      options?: RequestOptions,
    ): Promise<z.infer<S>> => parseWith(schema, await request(config(), "DELETE", path, undefined, options), path),
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/** The shared client every hook uses. `configureApiClient` changes where it points. */
export const api: ApiClient = createApiClient(() => sharedConfig);
